/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useEventLookups
 * ---------------
 * The three lookup tables the event wizard's pickers are built from.
 *
 * Fetched together in one query rather than three, because they are needed at
 * the same moment and a wizard that renders one picker at a time as each
 * request lands reads as broken.
 *
 * A table that fails is reported as an error AND returned as an empty lookup.
 * The distinction matters: the step still renders, the organizer can keep
 * filling in everything else, and validation blocks the save until the empty
 * picker is answered — which is better than a blank screen for a failure that
 * may be a single flaky request.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { readLookup } from '../components/athlohive/lookupOptions';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** Form-facing name → table name. */
const LOOKUP_TABLES = {
  activityTypes: 'activity_type',
  registrationTypes: 'registration_type',
  distanceUnits: 'distance_measuring_unit',
};

const EMPTY = { rows: [], labelKey: null };

const EMPTY_LOOKUPS = {
  activityTypes: EMPTY, registrationTypes: EMPTY, distanceUnits: EMPTY,
};

export default function useEventLookups(organizationId) {
  const query = useQuery({
    queryKey: ['eventLookups', organizationId],
    queryFn: async ({ signal }) => {
      const failures = [];
      const entries = await Promise.all(
        Object.entries(LOOKUP_TABLES).map(async ([key, tableName]) => {
          const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
            method: 'POST',
            signal,
            body: JSON.stringify({
              table_name: tableName,
              organization_id: Number(envConfig.organizationId),
              application_id: Number(envConfig.applicationId),
              page_size: 200,
            }),
          });
          if (!resp.ok) {
            failures.push(tableName);
            return [key, EMPTY];
          }
          return [key, readLookup(await resp.json())];
        })
      );
      return { lookups: Object.fromEntries(entries), failures };
    },
    // Nothing can be scoped until the organization is known, and firing early
    // would just cache an answer for the wrong key.
    enabled: organizationId != null,
    staleTime: 5 * 60 * 1000,
  });

  const lookups = useMemo(
    () => ({ ...EMPTY_LOOKUPS, ...(query.data?.lookups || {}) }),
    [query.data]
  );

  // A table that answered 200 with zero rows is not the same as one that
  // errored, and until now it was not reported at all — the request "succeeded",
  // so nothing warned, and the organizer got a dropdown containing only
  // "Choose a type" with no way to know why.
  //
  // It is a real and reachable state: these are shared reference tables whose
  // rows have no owner, so a row-level-security scope that filters by
  // created_by_id matches none of them. That is a platform configuration
  // problem rather than something the organizer can fix — but saying so beats
  // an empty list that looks like the feature is broken.
  const emptyTables = useMemo(() => Object.entries(LOOKUP_TABLES)
    .filter(([key]) => (lookups[key]?.rows?.length ?? 0) === 0)
    .map(([, tableName]) => tableName), [lookups]);

  const settled = organizationId != null && !query.isLoading && !query.isError;

  return {
    lookups,
    failedTables: query.data?.failures || [],
    // Loaded fine, but has nothing in it. Reported separately from failedTables
    // because the two need different words: one is "try again", the other is
    // "ask an administrator".
    emptyTables: settled ? emptyTables : [],
    isLoading: organizationId != null && query.isLoading,
    isError: query.isError || (query.data?.failures?.length ?? 0) > 0,
    isEmpty: settled && emptyTables.length > 0,
    refetch: query.refetch,
  };
}
